'use client';

import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@repo/ui/components/button';
import { LiquidSurface } from '@repo/ui/components/liquid-surface';
import { Skeleton } from '@repo/ui/components/skeleton';
import { StatusBadge } from '@repo/ui/components/status-badge';
import { useRetryAction } from '@/hooks/use-retry-action';
import { track } from '@/lib/analytics';

import type { HygieneCompleted } from '../types/hygiene';
import { HEALTH_REFERENCE } from '@/lib/health-reference';
import {
  AWAITING_ALLOCATION_NOTE,
  scoreAwaitsFirstAllocation,
  type HealthAssessment
} from '../utils/derive-health';
import { formatFixed } from '@/lib/format/number/fixed';
import { formatBasisPoints } from '@/lib/format/percent/basis-points';
import { formatPercent } from '@/lib/format/percent/percent';
import { DRIVER_STATE_PRIORITY } from '../utils/hygiene-presentation';
import { HygieneCategorySheet } from './hygiene-category-sheet';
import { DriverTile } from './portfolio-health-driver-tile';
import { MethodologyDialog } from './portfolio-health-methodology';
import { HealthRing, SignalDots } from './portfolio-health-rings';

interface PortfolioHealthCardProps {
  assessment: HealthAssessment;
  isLoading?: boolean;
  /**
   * The backend is preparing this wallet's first canonical assessment
   * (status `unavailable`, auto-queued). Renders a labeled waiting state —
   * never interim numbers that a completed assessment would then replace.
   */
  isPreparing?: boolean;
  /**
   * The first assessment is still not ready after the usual wait. The card
   * stops promising "under a minute" and says the wait is longer than usual.
   */
  isPreparingSlowly?: boolean;
  /**
   * The canonical assessment could not be loaded and none is cached. The
   * card reads "Assessment unavailable" with a retry; the browser never
   * reconstructs a score in its place.
   */
  isUnavailable?: boolean;
  onRetry?: () => void;
  isRetrying?: boolean;
  /**
   * The canonical assessment — when present, tiles open the per-category
   * check drill-down sheet.
   */
  hygiene?: HygieneCompleted;
  /**
   * The outcomes strip (live snapshot figures) rendered inside the card,
   * visually separate from the practice scores above it.
   */
  snapshot?: React.ReactNode;
}

/**
 * The Portfolio Health anchor (O03, node 1830:394 — resurfaced onto the
 * light liquid-glass system per product direction): overall score ring, the
 * weighted drivers with written states (the canonical categories, or the
 * fallback's four practices), and evidence coverage on a major
 * glass pane with a soft mint ambience. Advisory by design — the footer says
 * so in plain language. The page header already titles this surface, so the
 * card spends no space on its own header row.
 */
export function PortfolioHealthCard({
  assessment,
  isLoading = false,
  isPreparing = false,
  isPreparingSlowly = false,
  isUnavailable = false,
  onRetry,
  isRetrying = false,
  hygiene,
  snapshot
}: PortfolioHealthCardProps) {
  const coveragePct = assessment.coverageBps / 100;
  const showSkeleton = isLoading || isPreparing;
  // Nothing was assessed in either state, so nothing assessed is shown.
  const hasAssessment = !showSkeleton && !isUnavailable;
  const hasScore = assessment.score != null;
  // The id, not the category: a sheet left open across a refetch must show
  // the checks of the assessment on screen, not the snapshot it opened on.
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const retry = useRetryAction({
    label: 'Try again',
    onRetry: onRetry ?? (() => undefined),
    isRetrying
  });
  const openCategory =
    openCategoryId == null
      ? null
      : (hygiene?.categories.find(
          (candidate) => candidate.id === openCategoryId
        ) ?? null);
  // Reader-priority order: problems first, then healthy, then what's still
  // waiting on us — never the other way around.
  const orderedDrivers = useMemo(
    () =>
      [...assessment.drivers].sort(
        (left, right) =>
          (DRIVER_STATE_PRIORITY[left.state] ?? 9) -
            (DRIVER_STATE_PRIORITY[right.state] ?? 9) ||
          right.weight - left.weight
      ),
    [assessment.drivers]
  );

  return (
    <LiquidSurface
      type="major"
      role="region"
      aria-label="Portfolio health assessment"
      data-health-band={assessment.band}
      className="overflow-hidden p-6"
    >
      <div
        aria-hidden
        className="bg-rowa-brand-mint/50 pointer-events-none absolute -top-28 -right-20 size-80 rounded-full blur-3xl"
      />
      <div
        aria-hidden
        className="bg-rowa-brand-mint/30 pointer-events-none absolute -bottom-32 -left-24 size-72 rounded-full blur-3xl"
      />

      {hasAssessment && assessment.caps && assessment.caps.length > 0 ? (
        <div
          role="alert"
          className="border-rowa-status-error/25 bg-rowa-status-error/10 rounded-rowa-lg mb-5 border px-4 py-3"
        >
          <p className="text-rowa-label-md text-rowa-status-error font-medium">
            Score limited by a critical check
          </p>
          {assessment.caps.map((explanation, index) => (
            <p
              key={index}
              className="text-rowa-body-sm text-rowa-text-secondary mt-1"
            >
              {explanation}
            </p>
          ))}
        </div>
      ) : null}

      {isUnavailable ? (
        <div role="status" className="pb-6">
          <h2 className="text-rowa-card text-rowa-text-primary">
            Assessment unavailable
          </h2>
          <p className="text-rowa-body-sm text-rowa-text-secondary mt-1 max-w-xl">
            We could not load your portfolio health just now. Your holdings are
            not affected, and we never guess a score in its place. Try again in
            a moment.
          </p>
          {onRetry ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={retry.onClick}
              disabled={retry.disabled}
              loading={retry.loading}
              loadingLabel="Retrying"
              className="mt-4"
            >
              <RefreshCw aria-hidden />
              {retry.label}
            </Button>
          ) : null}
        </div>
      ) : isPreparing && isPreparingSlowly ? (
        <div role="status" className="pb-6">
          <h2 className="text-rowa-card text-rowa-text-primary">
            Your first assessment is taking longer than usual
          </h2>
          <p className="text-rowa-body-sm text-rowa-text-secondary mt-1 max-w-xl">
            We are still working on it and check again every minute. It will
            appear here as soon as it is ready. Nothing for you to do, and we
            never guess numbers while you wait.
          </p>
        </div>
      ) : showSkeleton ? (
        <div className="pb-6">
          {isPreparing ? (
            <p
              role="status"
              className="text-rowa-body-sm text-rowa-text-secondary mb-4"
            >
              Setting up this wallet&apos;s first assessment, usually under a
              minute. We never guess numbers while you wait.
            </p>
          ) : null}
          <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
            <div className="flex flex-col items-center gap-4">
              <Skeleton className="size-[126px] rounded-full" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton
                  key={index}
                  className="rounded-rowa-lg h-[104px] w-full"
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 pb-5 lg:grid-cols-[15rem_minmax(0,1fr)] lg:items-center">
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <p className="text-rowa-label-sm text-rowa-text-secondary self-start tracking-[0.08em] uppercase max-lg:self-center">
              Overall score
            </p>
            <div className="mt-3">
              <HealthRing assessment={assessment} />
            </div>
            <p className="text-rowa-label-sm text-rowa-text-brand mt-4 tracking-[0.08em] uppercase">
              Based on {assessment.drivers.length} areas
            </p>
            {hasScore ? (
              <>
                <div
                  className="bg-rowa-text-primary/10 mt-2 h-1.5 w-48 overflow-hidden rounded-full"
                  role="img"
                  aria-label={`${formatFixed(coveragePct, { digits: 0 })} percent of the scorecard has data`}
                >
                  <div className="flex h-full">
                    <div
                      className="bg-rowa-status-positive h-full rounded-full motion-safe:transition-[width] motion-safe:duration-700"
                      style={{ width: `${coveragePct}%` }}
                    />
                    {coveragePct < 100 ? (
                      <div
                        className="bg-rowa-status-warning/70 h-full rounded-r-full"
                        style={{ width: `${100 - coveragePct}%` }}
                      />
                    ) : null}
                  </div>
                </div>
                <p className="text-rowa-label-sm text-rowa-text-secondary mt-1.5">
                  {formatBasisPoints(assessment.coverageBps, {
                    fractionDigits: 0
                  })}{' '}
                  data
                </p>
              </>
            ) : (
              // The ring already shows coverage; explain the publication
              // gate instead of repeating the number with a second meter.
              // A wallet with no vault positions is not waiting on data.
              <p className="text-rowa-body-sm text-rowa-text-secondary mt-2 max-w-56 max-lg:mx-auto">
                {scoreAwaitsFirstAllocation(assessment) ? (
                  AWAITING_ALLOCATION_NOTE
                ) : (
                  <>
                    Your score appears when{' '}
                    {formatPercent(HEALTH_REFERENCE.minCoverageBps, {
                      from: 'bps',
                      digits: 0
                    })}{' '}
                    of the scorecard has data. Today:{' '}
                    {formatBasisPoints(assessment.coverageBps, {
                      fractionDigits: 0
                    })}
                    .
                  </>
                )}
              </p>
            )}
            <div className="mt-2 flex flex-wrap justify-center gap-1.5 lg:justify-start">
              {assessment.provisional ? (
                <StatusBadge tone="warning">
                  Early score, based on{' '}
                  {formatBasisPoints(assessment.coverageBps, {
                    fractionDigits: 0
                  })}{' '}
                  of the data
                </StatusBadge>
              ) : null}
              {assessment.refreshing ? (
                <StatusBadge tone="neutral">Updating…</StatusBadge>
              ) : null}
            </div>
            {assessment.confidence ? (
              <SignalDots confidence={assessment.confidence} />
            ) : null}
            {assessment.evidenceNotes && assessment.evidenceNotes.length > 0 ? (
              <ul className="mt-3 max-w-56 space-y-1.5 text-left max-lg:mx-auto">
                {assessment.evidenceNotes.map((note, index) => (
                  <li
                    key={index}
                    className="text-rowa-label-sm text-rowa-text-secondary"
                  >
                    {note}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <ul
            aria-label="Health drivers"
            className="grid list-none items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-3"
          >
            {orderedDrivers.map((entry) => {
              const category = hygiene?.categories.find(
                (candidate) => candidate.id === entry.id
              );
              return (
                <li key={entry.id} className="min-w-0">
                  <DriverTile
                    driver={entry}
                    onOpen={
                      category
                        ? () => {
                            track('overview.health_category_opened', {
                              category: category.id
                            });
                            setOpenCategoryId(category.id);
                          }
                        : undefined
                    }
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {snapshot ? (
        <div className="border-rowa-border-subtle border-t pt-4 pb-5">
          {snapshot}
        </div>
      ) : null}

      <div className="border-rowa-border-subtle flex flex-wrap items-center justify-between gap-2 border-t pt-4">
        {/* The dialog explains the assessment on screen. With none on
            screen there is nothing for it to explain. */}
        {hasAssessment ? (
          <MethodologyDialog assessment={assessment} hygiene={hygiene} />
        ) : (
          <span />
        )}
        <p className="text-rowa-label-sm text-rowa-text-tertiary">
          Suggestions never move funds. Review the impact before acting.
        </p>
      </div>

      <HygieneCategorySheet
        category={openCategory}
        hygiene={hygiene ?? null}
        onClose={() => setOpenCategoryId(null)}
      />
    </LiquidSurface>
  );
}
